<template>
  <div class="main-content-container reports-list-container">
    <div class="top-content-container">
      <div class="d-flex align-items-center mb-3">
        <b-icon
          icon="arrow-left"
          class="mr-2 back-arrow"
          font-scale="1.1"
          @click="goToReportsList"
          role="button"
          aria-label="Back"
        />
        <b-breadcrumb class="customer-breadcrumbs mb-0">
          <b-breadcrumb-item>
            <span
              @click="goToReportsList"
              class="text-uppercase font-family-medium"
            >
              Reports
            </span>
          </b-breadcrumb-item>
          <b-breadcrumb-item active>
            <span class="text-uppercase font-family-medium">
              {{ customerFullname }}
            </span>
          </b-breadcrumb-item>
        </b-breadcrumb>
      </div>

      <!-- Filters bar - matches the Customer summary page -->
      <div class="hr-bar">
        <div class="hr-field">
          <label>From</label>
          <input
            class="hr-input"
            type="date"
            v-model="fromDate"
            @change="onFromDateChange"
          />
        </div>
        <div class="hr-field">
          <label>To</label>
          <input
            class="hr-input"
            type="date"
            v-model="toDate"
            @change="onToDateChange"
          />
        </div>
        <!-- Single EA select, matching the Customer summary page: "All EA"
             plus this customer's assistants, selectedPA driving every fetch
             directly - no separate All EA/My data toggle to fall out of
             sync with it (that split let selectedPA keep filtering data
             even after switching back to "All EA"). -->
        <div class="hr-field" v-if="role === 'admin'">
          <label>EA</label>
          <select
            class="hr-select"
            v-model="selectedPA"
            @change="getSelectedPA"
          >
            <option
              v-for="opt in paNames"
              :key="opt.value"
              :value="opt.value"
              >{{ opt.text }}</option
            >
          </select>
        </div>
        <!-- EA data filter (for non-admin users only) -->
        <div class="hr-field" v-if="role !== 'admin'">
          <label>EA</label>
          <select
            class="hr-select"
            v-model="eaDataFilter"
            @change="onEADataFilterChange"
          >
            <option
              v-for="opt in eaDataFilterOptions"
              :key="opt.value"
              :value="opt.value"
              >{{ opt.text }}</option
            >
          </select>
        </div>
        <div class="hr-field">
          <label>Category</label>
          <select
            class="hr-select"
            v-model="selectedTaskType"
            @change="getSelectedCategory"
          >
            <option
              v-for="opt in taskTypes"
              :key="opt.value"
              :value="opt.value"
              >{{ opt.text }}</option
            >
          </select>
        </div>
      </div>
      <b-dropdown-divider class=""></b-dropdown-divider>
    </div>

    <!-- Left Graph row start -->

    <!-- Left Graph row end -->

    <b-row class="pt-5" no-gutters>
      <!-- Right table -->
      <b-col md="5" lg="12" xl="5">
        <b-row class="d-flex justify-content-between" no-gutters>
          <div class="d-flex flex-column">
            <h4 class="font-md primary--text font-family-medium">Summary</h4>
            <p class="secondary--text">
              {{ `${fromDate} to ${toDate}` }}
            </p>
          </div>
          <div
            class="d-flex flex-column ml-3 export-as-container"
            v-if="role === 'admin'"
          >
            <h4 class="font-md primary--text font-family-medium">Export as</h4>
            <div class="d-flex mt-2">
              <b-button class="btn-pill btn-danger px-4 action-btns mr-2">
                <h6 class="font-sm font-family-medium">PDF</h6>
              </b-button>
            </div>
          </div>
        </b-row>
      </b-col>
    </b-row>

    <b-row class="" no-gutters>
      <b-col
        xl="9"
        lg="12"
        md="9"
        class="mb-5 bar-graph-container"
        :class="[isLoadingBarChart ? 'px-0' : null]"
      >
        <div class="loader-wrapper" v-show="showLoader">
          <b-spinner />
        </div>
        <GChart
          ref="gChart"
          v-if="tasks.length > 0"
          :events="chartEvents"
          type="ColumnChart"
          :data="chartData"
          :options="chartOptions"
        />
        <div
          v-if="tasks.length === 0 && !showLoader"
          class="d-flex justify-content-center mt-5 align-items-center w-100 pt-5"
        >
          <h4 class="font-md primary--text font-family-medium mt-5">
            No reports found
          </h4>
        </div>
      </b-col>

      <b-col class="pl-4 pr-4" xl="3" lg="12" md="3">
        <b-card
          class="bg-none border-none mb-5"
          no-body
          v-if="!isLoadingRightPanel"
        >
          <b-row
            class="d-flex align-items-center justify-content-start flex-nowrap"
          >
            <b-avatar
              class="customer-avatar-reports"
              size="6rem"
              v-if="customerAvatar"
              :src="customerAvatar"
            ></b-avatar>
            <b-avatar
              size="6rem"
              v-else
              class="bg--primary customer-avatar-reports"
              :text="customerInitials"
            ></b-avatar>
            <div class="d-flex flex-column align-items-start ml-4">
              <p class="primary--text font-family-medium">
                {{ customerFullname }}
              </p>
              <!-- <p class="secondary--text customer--email reduce-whitespace">
                {{
                  customerEmail.length > 20
                    ? `${customerEmail.substring(0, 17)}...`
                    : customerEmail
                }}
              </p> -->
              <b-button
                class="reduce-whitespace-2 text-uppercase btn-chip btn-danger--light danger btn-chip--pill position-relative"
                >{{ customerMembershipType }}</b-button
              >
            </div>
          </b-row>

          <!-- Hours breakdown section -->
          <div class="hours-summary-box mt-4 ml-4 pr-3">
            <div class="hours-grid">
              <div class="hours-grid-item">
                <span class="hours-grid-label">Total Duration (hh:mm)</span>
                <span class="hours-grid-value font-family-medium">{{
                  totalDurationHHMM
                }}</span>
              </div>
              <div class="hours-grid-item">
                <span class="hours-grid-label">Billable Rounded</span>
                <span class="hours-grid-value font-family-medium"
                  >{{ billableRoundedHours }}
                  <span class="hours-grid-unit">hrs</span></span
                >
              </div>
              <div class="hours-grid-item">
                <span class="hours-grid-label">Billable Unrounded</span>
                <span class="hours-grid-value font-family-medium"
                  >{{ billableUnroundedHours }}
                  <span class="hours-grid-unit">hrs</span></span
                >
              </div>
              <div class="hours-grid-item">
                <span class="hours-grid-label">Non-Billable Rounded</span>
                <span class="hours-grid-value font-family-medium"
                  >{{ nonBillableRoundedHours }}
                  <span class="hours-grid-unit">hrs</span></span
                >
              </div>
              <div class="hours-grid-item">
                <span class="hours-grid-label">Non-Billable Unrounded</span>
                <span class="hours-grid-value font-family-medium"
                  >{{ nonBillableUnroundedHours }}
                  <span class="hours-grid-unit">hrs</span></span
                >
              </div>
            </div>
          </div>

          <template v-if="role === 'admin'">
            <b-dropdown-divider class="mt-5 ml-3 "></b-dropdown-divider>
            <template v-if="assistantsList.length > 0">
              <h4 class="font-md secondary--text mt-4 ml-4">Assistants</h4>
              <div
                v-for="assistant in assistantsList.slice(0, 3)"
                :key="assistant.id"
                class="d-flex align-items-center mt-3 ml-4"
              >
                <b-avatar
                  size="4.4rem"
                  class="customer-avatar"
                  :class="[!assistant.avatarThumbnail ? 'bg--primary' : null]"
                  :src="
                    assistant.avatarThumbnail ? assistant.avatarThumbnail : null
                  "
                  :text="
                    !assistant.avatarThumbnail
                      ? assistant.assistantInitials
                      : null
                  "
                ></b-avatar>
                <p class="primary--text font-family-medium ml-4 mt-2">
                  {{
                    assistant.assistantFullName.length > 15
                      ? `${assistant.assistantFullName.substring(0, 12)}...`
                      : `${assistant.assistantFullName}`
                  }}
                </p>
              </div>
              <h6
                class="font-sm primary--text ml-4 mt-2"
                v-if="assistantsList.length > 3"
              >
                +
                {{ assistantsList.length - assistantsList.slice(0, 3).length }}
                more
              </h6>
            </template>
            <template v-else>
              <div
                class="no-assigned-msg pt-5 d-flex flex-column justify-content-center align-items-center"
              >
                <h4 class="font-md primary--text font-family-medium">
                  No assistants found.
                </h4>
              </div>
            </template>
          </template>
        </b-card>
        <b-card v-else class="bg-none border-none" no-body>
          <b-row
            class="d-flex align-items-center justify-content-start flex-nowrap"
          >
            <b-skeleton
              class="customer-avatar-reports"
              type="avatar"
              size="8rem"
            ></b-skeleton>

            <div class="ml-4 w-50">
              <b-skeleton width="60%" class="mb-2"></b-skeleton>
              <b-skeleton width="100%" class="mb-2"></b-skeleton>
              <b-skeleton width="40%"></b-skeleton>
            </div>
          </b-row>
          <div class="hours-summary-box mt-4 ml-4 pr-3">
            <div class="hours-grid">
              <div
                class="hours-grid-item"
                v-for="i in 5"
                :key="'skeleton-hour-' + i"
              >
                <b-skeleton width="70%" class="mb-2"></b-skeleton>
                <b-skeleton width="40%"></b-skeleton>
              </div>
            </div>
          </div>
          <template v-if="role === 'admin'">
            <b-dropdown-divider class="mt-5 mb-5 ml-3 "></b-dropdown-divider>
            <div
              v-for="(n, index) in 3"
              :key="index"
              class="d-flex align-items-center my-3 ml-3"
            >
              <b-skeleton type="avatar" size="4.4rem"></b-skeleton>
              <b-skeleton width="50%" class="ml-4"></b-skeleton>
            </div>
          </template>
        </b-card>
      </b-col>

      <b-col xl="12" lg="12" md="12">
        <b-card
          no-body
          class="mb-5 px-3"
          v-if="!isLoadingTaskList && tasks.length > 0"
        >
          <b-card-body class="px-5 py-5 mb-2">
            <b-tabs v-model="tabIndex" active-nav-item-class="danger">
              <b-tab title="Task List">
                <TaskListReport
                  :customerId="this.$route.params.profileId"
                  :minDate="this.minToDate"
                  :maxDate="this.maxFromDate"
                  :categories="selectedTaskType"
                  :assignedTo="selectedPA"
                />
              </b-tab>
              <b-tab title="Chart breakdown">
                <PieChartReport
                  v-if="categorisedTasks.length > 0"
                  :categories="categorisedTasks"
                />
              </b-tab>
            </b-tabs>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import TaskListReport from "./TaskListReport";
import PieChartReport from "./PieChartReport";
import moment from "moment";
import { mapState } from "vuex";
import axios from "@/config/axios";
import momentDurationFormatSetup from "moment-duration-format";
import {
  downloadReports,
  getCustomerReportDetails,
  getCustomerReportGraphs,
  getReportDetailsTasks
} from "@/apis/customers";
import { getCategories } from "@/apis/common";
import { BASE_URL, DEBOUNCE_DELAY } from "@/utils/constants";
import _ from "lodash";
import { getProfile } from "@/apis/auth";
momentDurationFormatSetup(moment);

export default {
  name: "ReportDetails",
  components: {
    TaskListReport,
    PieChartReport
  },
  computed: {
    minToDate() {
      if (this.fromDate === "") return null;
      return this.fromDate;
    },
    maxFromDate() {
      if (this.toDate === "") return null;
      return this.toDate;
    },
    fastExportUrl() {
      let { fromDate, toDate } = this;
      let token = localStorage.getItem("user-token");
      let customerId = this.$route.params.profileId;

      return axios.getUri({
        url: `${BASE_URL}/reports/customers/export-csv`,
        params: {
          completed_gte: fromDate,
          completed_lte: toDate,
          token: token,
          assigned_to: this.selectedPA === "" ? null : this.selectedPA,
          categories:
            this.selectedTaskType === "" ? null : this.selectedTaskType,
          created_for: customerId,
          include_null_timer: this.csvNullvalues
        }
      });
    },
    showLoader() {
      return !this.isChartReady && this.isLoadingBarChart;
    },
    ...mapState("authModule", ["role", "profileUserId"])
  },
  data() {
    return {
      generating: false,
      reportDetails: [],
      isLoadingBarChart: true,
      isChartReady: false,
      tabIndex: 0,
      durationList: [],
      categorisedTasks: [],
      legendTitles: [],
      chartData: [
        [
          "Date",
          "Billable",
          { type: "string", role: "tooltip", p: { html: true } }
        ]
      ],
      chartEvents: {
        ready: () => {
          let chartLegends = this.$refs.gChart.data;
          for (let i = 1; i < chartLegends.length; i++) {
            this.legendTitles.push(chartLegends[i]);
          }

          this.taskDetails = this.legendTitles.map(
            ([date, duration, tooltip]) => ({
              date,
              duration,
              tooltip
            })
          );
          let durationArr = this.taskDetails.map(item => item.duration);
          durationArr.forEach(item => {
            this.durationList.push(this.getDurationInHoursAndMinutes(item));
          });
          this.isChartReady = true;
        }
      },
      chartOptions: {
        chart: {
          title: "Customer Report",
          subtitle: "Customer Report List"
        },
        backgroundColor: "#F6F9FC",
        height: 400,
        tooltip: {
          isHtml: true
        },
        colors: ["#9FCAE2", "#074F89"],
        legend: {
          position: "top",
          alignment: "end",
          textStyle: {
            fontName: "Graphik Regular",
            color: "#aaa",
            fontSize: 12
          }
        },
        hAxis: {
          textStyle: {
            color: "#aaa"
          }
        },
        vAxis: {
          textStyle: {
            color: "#aaa"
          },
          ticks: []
        }
        // chartArea: {
        //   left: "3%",
        // },
      },
      // New filter for EA users to toggle between all EAs data and their own data
      eaDataFilter: "all",
      eaDataFilterOptions: [
        { text: "All EAs", value: "all" },
        { text: "My Work", value: "my" }
      ],
      selectedPA: "",
      selectedTaskType: "",
      fromDate: moment
        .utc()
        .subtract(1, "month")
        .format("YYYY-MM-DD"),
      toDate: moment.utc().format("YYYY-MM-DD"),
      paNames: [
        {
          text: "All EA",
          value: null
        }
      ],
      taskTypes: [
        {
          text: "Task type",
          value: ""
        }
      ],
      customerFullname: "",
      customerAvatar: "",
      customerInitials: "",
      params: {},
      customerEmail: "",
      customerMembershipType: "",
      assistantsList: [],
      tasks: [],
      // Same source (billableHours.totalRoundedDuration) and formatter
      // (getDurationInHoursAndMinutes) as the "Total Duration (hh:mm)"
      // column on the customer summary table - decimal "1.17 hrs" here
      // vs. "01:10" there was the same total shown two different ways,
      // reading as a mismatch. Keep both hh:mm so they visibly agree.
      totalDurationHHMM: "00:00",
      billableRoundedHours: "0.00",
      billableUnroundedHours: "0.00",
      nonBillableRoundedHours: "0.00",
      nonBillableUnroundedHours: "0.00",
      isLoadingRightPanel: true,
      totalHours: "",
      isLoadingTaskList: true,
      roleName: "",
      returnQuery: {},
      csvNullvalues: false
    };
  },
  mounted() {
    let { assistantId, taskType } = this.$route.query;
    // carrying over filters from the prev page
    if (assistantId) {
      this.selectedPA = assistantId;
    }
    if (taskType) {
      this.selectedTaskType = taskType;
    }
    // Keep the exact query ReportsList sent so the back arrow/breadcrumb
    // can push straight back to that filtered view - relying on
    // $router.back() alone breaks on a deep link, reload, or new tab.
    this.returnQuery = { ...this.$route.query };
    getProfile()
      .then(res => {
        this.roleName = res.data.role;

        // For non-admin users (EAs), restrict to their own data
        if (this.roleName !== "admin") {
          this.selectedPA = this.profileUserId;
        }

        this.fromDate = this.$route.params.from;
        this.toDate = this.$route.params.to;

        if (this.roleName === "admin") {
          this.params = {
            completed_lte: this.toDate,
            completed_gte: this.fromDate,
            created_for: this.$route.params.profileId,
            categories: this.selectedTaskType,
            assigned_to: this.selectedPA
          };
        } else if (this.roleName === "assistant") {
          this.params = {
            completed_lte: this.toDate,
            completed_gte: this.fromDate,
            created_for: this.$route.params.profileId,
            categories: this.selectedTaskType,
            assigned_to: this.profileUserId
          };
        }

        getCustomerReportDetails(
          this.$route.params.profileId,
          this.fromDate,
          this.toDate,
          this.selectedTaskType,
          this.selectedPA
        ).then(res => {
          this.paNames = [
            ...res.data.assignedAssistants,
            ...res.data.handoverAssistants
          ];
          this.paNames.map(item => {
            item.value = item.id;
            item.text = `${item.firstName} ${item.lastName}`;
          });
          this.paNames.unshift({ text: "All EA", value: "" });
        });

        this.getReportDetails();
        this.getReportsGraph();
        this.getTaskTypes();
        this.getTaskList();
      })
      .catch(err => {
        console.log(err);
      });

    this.onFromDateChange = _.debounce(this.onFromDateChange, DEBOUNCE_DELAY);
    this.onToDateChange = _.debounce(this.onToDateChange, DEBOUNCE_DELAY);
  },
  methods: {
    goToReportsList() {
      this.$router.push({ name: "ReportsList", query: this.returnQuery });
    },
    // Native <input type="date"> already guarantees YYYY-MM-DD; only the
    // range order needs checking here (mirrors ReportsList's filter bar).
    datesValid() {
      return !this.fromDate || !this.toDate || this.fromDate <= this.toDate;
    },
    onToDateChange() {
      if (this.datesValid()) this.setToDate();
    },
    onFromDateChange() {
      if (this.datesValid()) this.setFromDate();
    },
    downloadCSV() {
      this.generating = true;
      this.paramsDownload = {
        completed_lte: this.toDate,
        completed_gte: this.fromDate,
        created_for: this.$route.params.profileId,
        token: localStorage.getItem("user-token")
      };
      downloadReports(this.paramsDownload)
        .then(res => {
          let csv = res.data;
          var csvData =
            "data:application/csv;charset=utf-8," + encodeURIComponent(csv);
          let anchor = document.createElement("a");
          anchor.setAttribute("download", "Reports.csv");
          anchor.setAttribute("href", csvData);
          anchor.click();
          this.generating = false;
        })
        .catch(err => {
          console.log(err);
        });
    },
    setFromDate() {
      this.getReportDetails();
      this.getReportsGraph();
      this.getTaskList();
    },
    setToDate() {
      this.getReportDetails();
      this.getReportsGraph();
      this.getTaskList();
    },
    getSelectedPA() {
      this.getReportDetails();
      this.getReportsGraph();
      this.getTaskList();
    },
    getSelectedCategory() {
      this.getReportDetails();
      this.getReportsGraph();
      this.getTaskList();
    },
    onEADataFilterChange() {
      this.getReportDetails();
      this.getReportsGraph();
      this.getTaskList();
    },
    getTaskTypes() {
      getCategories()
        .then(res => {
          this.taskTypes = [...res.data.results].map(item => {
            return {
              text: item.title,
              value: item.id
            };
          });
          this.taskTypes.unshift({
            text: "Task type",
            value: ""
          });
        })
        .catch(err => {
          console.log(err);
        });
    },
    getReportDetails() {
      this.categorisedTasks = [];
      this.isLoadingRightPanel = true;
      // For admin: always filter by selected EA to show only their customers
      // For EA users: eaDataFilter controls whether to show all EAs data or just their data
      let assistantId;
      if (this.role === "admin") {
        assistantId = this.selectedPA === "" ? null : this.selectedPA;
      } else {
        // For EA users:
        // "All EAs" → assigned_to = null (show all EAs data for their assigned customers)
        // "My Work" → assigned_to = profileUserId (show only this EA's data)
        assistantId = this.eaDataFilter === "all" ? null : this.profileUserId;
      }
      getCustomerReportDetails(
        this.$route.params.profileId,
        this.fromDate,
        this.toDate,
        this.selectedTaskType,
        assistantId
      )
        .then(res => {
          this.categorisedTasks = [...res.data.categorisedTasks];
          this.reportDetails = res.data;
          let { profile, billableHours } = res.data;
          const isRealCustomer =
            profile.email !== "developer@iloveheadroom.com";
          this.totalDurationHHMM = this.getDurationInHoursAndMinutes(
            isRealCustomer ? billableHours.totalRoundedDuration : 0
          );
          // Billable / Non-billable breakdown
          this.billableRoundedHours = (
            billableHours.totalRoundedBillableHours || 0
          ).toFixed(2);
          this.billableUnroundedHours = (
            billableHours.totalBillableHours || 0
          ).toFixed(2);
          this.nonBillableRoundedHours = (
            billableHours.totalRoundedNonBillableHours || 0
          ).toFixed(2);
          this.nonBillableUnroundedHours = (
            billableHours.totalNonBillableHours || 0
          ).toFixed(2);
          this.customerFullname = `${profile.firstName} ${profile.lastName}`;
          this.customerInitials = this.customerFullname
            .match(/\b(\w)/g)
            .join("");
          this.customerEmail = profile.email;
          this.totalHours = profile.membershipHours;
          if (profile.membershipHours === 0) {
            this.totalHours = "N/A";
          }
          this.customerAvatar = profile.avatarThumbnail;
          this.customerMembershipType = profile.membership;
          this.assistantsList = [
            ...res.data.assignedAssistants,
            ...res.data.handoverAssistants
          ];
          this.assistantsList.map(item => {
            item.assistantFullName = `${item.firstName} ${item.lastName}`;
            item.assistantInitials = item.assistantFullName
              .match(/\b(\w)/g)
              .join("");
            return item;
          });
        })
        .catch(err => {
          console.log(err);
        })
        .finally(() => {
          this.isLoadingRightPanel = false;
        });
    },
    async getTaskList() {
      this.isLoadingTaskList = true;
      let { profileId } = this.$route.params;
      // completed_lte=${to}&completed_gte=${from}&categories=${category}&assigned_to=${assignedTo}`,
      try {
        let assignedTo;
        if (this.role === "admin") {
          assignedTo = this.selectedPA === "" ? null : this.selectedPA;
        } else {
          // For EA users: eaDataFilter controls whether to show all EAs or just their data
          assignedTo = this.eaDataFilter === "all" ? null : this.profileUserId;
        }
        let apiResponse = await getReportDetailsTasks(profileId, {
          completed_gte: this.fromDate,
          completed_lte: this.toDate,
          categories:
            this.selectedTaskType === "" ? null : this.selectedTaskType,
          assigned_to: assignedTo
        });
        this.tasks = apiResponse.data.results;
      } catch (e) {
        console.log(e);
      } finally {
        this.isLoadingTaskList = false;
      }
    },
    getReportsGraph() {
      this.isLoadingBarChart = true;
      this.isChartReady = false;
      this.chartData = [
        [
          "Date",
          "Billable-Actual Hours",
          { type: "string", role: "tooltip", p: { html: true } },
          "Non-billable-Actual Hours",
          { type: "string", role: "tooltip", p: { html: true } }
        ]
      ];
      if (this.role === "admin") {
        const assignedTo = this.selectedPA === "" ? null : this.selectedPA;
        this.params = {
          completed_lte: this.toDate,
          completed_gte: this.fromDate,
          created_for: this.$route.params.profileId,
          categories: this.selectedTaskType,
          assigned_to: assignedTo
        };
      } else if (this.role === "assistant") {
        // For EA users: eaDataFilter controls whether to show all EAs or just their data
        const assignedTo = this.eaDataFilter === "all" ? null : this.profileUserId;
        this.params = {
          completed_lte: this.toDate,
          completed_gte: this.fromDate,
          created_for: this.$route.params.profileId,
          categories: this.selectedTaskType,
          assigned_to: assignedTo
        };
      }
      getCustomerReportGraphs(this.params)
        .then(res => {
          let max =
            res.data.highestBillableTotal > res.data.highestNonbillableTotal
              ? res.data.highestBillableTotal
              : res.data.highestNonbillableTotal;
          res.data.trackers.map(item => {
            this.chartData.push([
              moment(item.date).format("DD/MM"),
              item.totalRoundedBillableTime / 3600,
              this.getCustomTooltip(
                moment(item.date).format("DD/MM/YY"),
                item.totalRoundedBillableTime
              ),
              item.totalRoundedNonbillableTime / 3600,
              this.getCustomTooltip(
                moment(item.date).format("DD/MM/YY"),
                item.totalRoundedNonbillableTime
              )
            ]);
          });
          // Ceil to the nearest 20-hour block (default 0-20h axis, 2h
          // gridlines) so a day that exceeds 20h still grows the axis in
          // the same 2h-per-step proportion instead of clipping bars.
          max = Math.ceil(max / (3600 * 20)) * 20;
          max /= 10;
          // let scale = [];
          this.chartOptions.vAxis.ticks = [];
          for (let i = 0; i <= 10; i++) {
            let seconds = max * i;
            let hours = (max * i).toString();
            this.chartOptions.vAxis.ticks.push({
              v: seconds,
              f: hours
            });
          }
        })
        .catch(e => {
          console.log(e);
        })
        .finally(() => {
          this.isLoadingBarChart = false;
        });
    },
    onContextFromDate(ctx) {
      // The following will be an empty string until a valid date is entered
      this.fromDate = ctx.selectedYMD;
      // this.getReportDetails();
    },
    onContextToDate(ctx) {
      // The following will be an empty string until a valid date is entered
      this.toDate = ctx.selectedYMD;
      // this.getReportDetails();
    },
    getCustomTooltip(title, seconds) {
      const hrsAndMins = (seconds / 3600).toFixed(2);
      return `<h6 class="primary--text font-sm  font-family-medium ml-3 py-3">${title}</h6>
          <h6 class="primary--text font-sm  font-family-medium ml-3"> ${hrsAndMins} Hours</h6>`;
    },
    getDurationInHours(seconds) {
      return moment.duration(seconds, "seconds").format("hh:mm", {
        trim: false
      });
    },
    getDurationInHoursAndMinutes(seconds) {
      return moment.duration(seconds, "seconds").format("hh:mm", {
        trim: false
      });
    }
  }
};
</script>

<style scoped lang="scss">
@import "~@/scss/variables/colors.scss";

.export-as-container {
  margin-top: -0.5rem;
}
.action-btns {
  height: 2rem;
}
.back-arrow {
  cursor: pointer;
  color: #074f89;
  flex-shrink: 0;
}
.reports-list-container {
  margin-bottom: 10rem;
}
.bar-graph-container {
  min-height: 40rem;
  border: 1px solid #dcebf4;
  overflow: hidden;
}
.dateheight {
  height: 1rem !important;
}
.customer-avatar-reports {
  margin-left: 2rem;
}
.customer--email {
  width: 18rem;
}
.loader-wrapper {
  position: absolute;
  background: $bg--light;
  z-index: 50;
  width: 100%;
  height: 100%;
  opacity: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  > span {
    width: 5rem;
    height: 5rem;
  }
}
.hours-summary-box {
  margin-top: 1.5rem;
}
.hours-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: auto auto;
  border: 1px solid #dcebf4;
  border-radius: 8px;
  overflow: hidden;
  background-color: #ffffff;
}
.hours-grid-item {
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
/* Item 1 (Total Duration) spans the full width as a header row above the
   billable/non-billable x rounded/unrounded 2x2 grid below it. */
.hours-grid-item:nth-child(1) {
  grid-column: 1 / -1;
  border-bottom: 1px solid #dcebf4;
}
.hours-grid-item:nth-child(2),
.hours-grid-item:nth-child(3) {
  border-bottom: 1px solid #dcebf4;
}
.hours-grid-item:nth-child(2),
.hours-grid-item:nth-child(4) {
  border-right: 1px solid #dcebf4;
}
.hours-grid-label {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c8ea4;
  margin-bottom: 0.5rem;
  font-weight: 600;
}
.hours-grid-value {
  font-size: 1.5rem;
  color: #074f89;
  font-weight: 700;
}
.hours-grid-unit {
  font-size: 0.95rem;
  color: #6c8ea4;
  font-weight: 400;
  margin-left: 0.15rem;
}
</style>
